'use client';

import { c as _c } from "react/compiler-runtime";
import { motion } from 'framer-motion';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import useBreakpoints from '@/hooks/useBreakpoints';
import { Button } from '@/shared/components/Button';
import { Modal } from '@/utils/antd-imports';
import { CelebrationCanvas } from './CelebrationCanvas';
interface VideoWatchSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVoteMore: () => void;
  onBack: () => void;
  rewardAmount?: number;
}

/**
 * @description Модальное окно успешного просмотра видео с анимацией празднования.
 * При появлении показывает canvas-анимацию (звёзды + конфетти) и
 * framer-motion анимации для контента (scale, fade, stagger).
 *
 * @example
 * <VideoWatchSuccessModal
 *   isOpen={true}
 *   onClose={handleClose}
 *   onVoteMore={handleVoteMore}
 *   onBack={handleBack}
 *   rewardAmount={150}
 * />
 */
export const VideoWatchSuccessModal: FC<VideoWatchSuccessModalProps> = t0 => {
  const $ = _c(59);
  const {
    isOpen,
    onClose,
    onVoteMore,
    onBack,
    rewardAmount: t1
  } = t0;
  const rewardAmount = t1 === undefined ? 150 : t1;
  const t = useTranslations("VideoWatch.Success");
  const {
    lg,
    tablet
  } = useBreakpoints();
  if (!isOpen) {
    return null;
  }
  const t2 = lg ? "32px 24px" : tablet ? "28px 24px " : "20px";
  let t3;
  if ($[0] !== t2) {
    t3 = {
      body: {
        display: "flex",
        flexDirection: "column",
        height: "auto",
        padding: t2,
        minHeight: "auto",
        background: "#1E1E2A",
        backdropFilter: "blur(36.85px)",
        borderRadius: "24px",
        position: "relative",
        overflow: "hidden"
      }
    };
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <CelebrationCanvas />;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const t5 = `absolute ${lg ? "right-[16px] top-[16px]" : "right-[12px] top-[12px]"} z-20 flex items-center justify-center rounded-full border border-[#E9ECF033] p-2 transition-colors hover:bg-white/10`;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Image src="/icons/close-default.svg" width={12} height={12} alt="Close" />;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== onClose || $[5] !== t5) {
    t7 = <button type="button" onClick={onClose} className={t5}>{t6}</button>;
    $[4] = onClose;
    $[5] = t5;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      opacity: 0,
      scale: 0.8
    };
    t9 = {
      opacity: 1,
      scale: 1
    };
    $[7] = t8;
    $[8] = t9;
  } else {
    t8 = $[7];
    t9 = $[8];
  }
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      duration: 0.5,
      ease: [0.34, 1.56, 0.64, 1]
    };
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  let t12;
  let t13;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      opacity: 0,
      y: -20
    };
    t12 = {
      opacity: 1,
      y: 0
    };
    t13 = {
      delay: 0.2,
      duration: 0.5
    };
    $[10] = t11;
    $[11] = t12;
    $[12] = t13;
  } else {
    t11 = $[10];
    t12 = $[11];
    t13 = $[12];
  }
  let t14;
  if ($[13] !== t) {
    t14 = t("Congratulations");
    $[13] = t;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  const t15 = `${t14}!`;
  let t16;
  if ($[15] !== t15) {
    t16 = <motion.h2 className="text-[20px] font-bold text-white tablet:text-[28px] lg:text-[36px]" initial={t11} animate={t12} transition={t13}>{t15}</motion.h2>;
    $[15] = t15;
    $[16] = t16;
  } else {
    t16 = $[16];
  }
  let t17;
  let t18;
  let t19;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      opacity: 0,
      y: 20
    };
    t18 = {
      opacity: 1,
      y: 0
    };
    t19 = {
      delay: 0.4,
      duration: 0.5
    };
    $[17] = t17;
    $[18] = t18;
    $[19] = t19;
  } else {
    t17 = $[17];
    t18 = $[18];
    t19 = $[19];
  }
  let t20;
  if ($[20] !== t) {
    t20 = t("Title");
    $[20] = t;
    $[21] = t20;
  } else {
    t20 = $[21];
  }
  let t21;
  if ($[22] !== t20) {
    t21 = <span className="text-[16px] font-medium text-[#E9ECF0CC] tablet:text-[20px]">{t20}</span>;
    $[22] = t20;
    $[23] = t21;
  } else {
    t21 = $[23];
  }
  let t22;
  let t23;
  let t24;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      scale: 0
    };
    t23 = {
      scale: 1
    };
    t24 = {
      delay: 0.6,
      type: "spring",
      stiffness: 200,
      damping: 10
    };
    $[24] = t22;
    $[25] = t23;
    $[26] = t24;
  } else {
    t22 = $[24];
    t23 = $[25];
    t24 = $[26];
  }
  let t25;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Image src="/images/actions/gem.png" alt="Coin" width={32} height={32} />;
    $[27] = t25;
  } else {
    t25 = $[27];
  }
  let t26;
  if ($[28] !== rewardAmount) {
    t26 = <motion.div className="flex items-center gap-2" initial={t22} animate={t23} transition={t24}><div className="flex items-center gap-2 rounded-full bg-[#38384766] px-4 py-3">{t25}<span className="text-[28px] font-semibold text-white">{rewardAmount}</span></div></motion.div>;
    $[28] = rewardAmount;
    $[29] = t26;
  } else {
    t26 = $[29];
  }
  let t27;
  if ($[30] !== t21 || $[31] !== t26) {
    t27 = <motion.div className="flex flex-col items-center gap-3 tablet:gap-4" initial={t17} animate={t18} transition={t19}>{t21}{t26}</motion.div>;
    $[30] = t21;
    $[31] = t26;
    $[32] = t27;
  } else {
    t27 = $[32];
  }
  let t28;
  let t29;
  let t30;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      opacity: 0,
      y: 20
    };
    t29 = {
      opacity: 1,
      y: 0
    };
    t30 = {
      delay: 0.8,
      duration: 0.4
    };
    $[33] = t28;
    $[34] = t29;
    $[35] = t30;
  } else {
    t28 = $[33];
    t29 = $[34];
    t30 = $[35];
  }
  let t31;
  if ($[36] !== t) {
    t31 = t("VoteMore");
    $[36] = t;
    $[37] = t31;
  } else {
    t31 = $[37];
  }
  let t32;
  if ($[38] !== onVoteMore || $[39] !== t31) {
    t32 = <Button variant="whiteSecondary" size="md_a" className="w-full" onClick={onVoteMore}>{t31}</Button>;
    $[38] = onVoteMore;
    $[39] = t31;
    $[40] = t32;
  } else {
    t32 = $[40];
  }
  let t33;
  if ($[41] !== t) {
    t33 = t("Back");
    $[41] = t;
    $[42] = t33;
  } else {
    t33 = $[42];
  }
  let t34;
  if ($[43] !== onBack || $[44] !== t33) {
    t34 = <Button variant="whiteSecondaryOutline" size="md_a" className="w-full" onClick={onBack}>{t33}</Button>;
    $[43] = onBack;
    $[44] = t33;
    $[45] = t34;
  } else {
    t34 = $[45];
  }
  let t35;
  if ($[46] !== t32 || $[47] !== t34) {
    t35 = <motion.div className="mt-4 flex w-full flex-col items-center gap-4 tablet:mt-5 lg:flex-row-reverse" initial={t28} animate={t29} transition={t30}>{t32}{t34}</motion.div>;
    $[46] = t32;
    $[47] = t34;
    $[48] = t35;
  } else {
    t35 = $[48];
  }
  let t36;
  if ($[49] !== t16 || $[50] !== t27 || $[51] !== t35) {
    t36 = <motion.div className="relative z-10 flex flex-col items-center justify-center gap-4 tablet:gap-5" initial={t8} animate={t9} transition={t10}>{t16}{t27}{t35}</motion.div>;
    $[49] = t16;
    $[50] = t27;
    $[51] = t35;
    $[52] = t36;
  } else {
    t36 = $[52];
  }
  let t37;
  if ($[53] !== isOpen || $[54] !== onClose || $[55] !== t3 || $[56] !== t36 || $[57] !== t7) {
    t37 = <Modal open={isOpen} onCancel={onClose} footer={null} destroyOnHidden={true} centered={true} className="max-w-[288px] tablet:max-w-[400px] lg:max-w-[560px] [&_.ant-modal-content]:!bg-transparent" styles={t3} closable={false}>{t4}{t7}{t36}</Modal>;
    $[53] = isOpen;
    $[54] = onClose;
    $[55] = t3;
    $[56] = t36;
    $[57] = t7;
    $[58] = t37;
  } else {
    t37 = $[58];
  }
  return t37;
};